import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    message,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
    Upload,
} from 'antd';
import {
    CalendarOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    InfoCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SolutionOutlined,
    TagsOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import TipTapEditor from './TipTapEditor';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import { getDropdownValues } from '../services/moduleDataApi';
import { getFormGroups, createModuleRecord } from '../services/addFormApi';
import { getAdminClients, getCustomForms } from '../services/adminApi';
import { updateRecord } from '../services/detailedViewApi';
import { buildPayload, buildInitialValue, collectFileParts, effectiveDataType, getDeep } from './form/payloadTransformer';
import AppBreadcrumb from '../components/Breadcrumb';

const { Text } = Typography;
const { TextArea } = Input;
const emptyGroups = [];

const sectionIcons = [
    <UserOutlined />,
    <EnvironmentOutlined />,
    <SolutionOutlined />,
    <InfoCircleOutlined />,
    <FileTextOutlined />,
];

const fieldIcons = {
    email: <MailOutlined className="v1-input-icon" />,
    contactNumber: <PhoneOutlined className="v1-input-icon" />,
    phone: <PhoneOutlined className="v1-input-icon" />,
    currentLocation: <EnvironmentOutlined className="v1-input-icon" />,
    preferredLocation: <EnvironmentOutlined className="v1-input-icon" />,
    skills: <TagsOutlined className="v1-input-icon" />,
    firstName: <UserOutlined className="v1-input-icon" />,
    lastName: <UserOutlined className="v1-input-icon" />,
};

const truthy = (value) => value === true || value === 1 || value === '1';
const editable = (field) => field?.edit === undefined || truthy(field.edit);

function normalizeOptions(options = []) {
    return options.map((option) => {
        if (typeof option === 'string') return { label: option, value: option };
        return {
            label: option.label ?? option.name ?? option.value,
            value: option.value ?? option.id ?? option.name ?? option.label,
        };
    });
}

function extractGroups(formGroups) {
    if (Array.isArray(formGroups)) return formGroups;
    if (Array.isArray(formGroups?.groups)) return formGroups.groups;
    if (Array.isArray(formGroups?.data)) return formGroups.data;
    if (Array.isArray(formGroups?.data?.groups)) return formGroups.data.groups;
    return emptyGroups;
}

function normalizeGroups(formGroups = []) {
    return [...extractGroups(formGroups)]
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
        .map((group) => ({
            ...group,
            fields: [...(group.fields ?? [])]
                // Drop hidden fields, and drop any client-scope "gate" field — it is
                // rendered as the top "Select Client" picker, never as a body field,
                // so the client name is not duplicated as a separate card.
                .filter((field) => (field.show === undefined || truthy(field.show)) && !truthy(field.clientScope))
                .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
        }))
        .filter((group) => group.fields.length);
}

// pickPrefillValue reads a field's initial value from a prefill SOURCE record,
// honouring the admin-config `prefillFrom` mapping FIRST (so a target field can
// inherit from a differently-named source key — e.g. submission.totalExperience
// ← candidate.yearOfExperience), then falling back to field.field and payloadKey.
// Tolerates both flat dotted keys (the getFormGroups flat record) and nested
// objects (addRow rows). Entirely config-driven — no field-name logic here.
function pickPrefillValue(record, field) {
    if (!record || !field) return undefined;
    for (const path of [field.prefillFrom, field.field, field.payloadKey]) {
        if (!path) continue;
        let v = record[path];
        if (v === undefined || v === null) v = getDeep(record, path);
        if (v !== undefined && v !== null) return v;
    }
    return undefined;
}

// Convert a stored file-reference object to an Ant Design fileList entry so
// file fields pre-show previously uploaded files when prefilling. Handles:
//   resume:   { location, uploadedFileName, uniqueName }
//   passport: { passportCopyLocation, passportUploadName, … }
//   visa/alternateVisa: same *CopyLocation / *UploadName pattern
function toFileListValue(value) {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object') return [];
    const name =
        value.uploadedFileName ??
        value.name ??
        Object.entries(value).find(([k]) => k.endsWith('UploadName'))?.[1] ??
        '';
    const url =
        value.location ??
        value.url ??
        Object.entries(value).find(([k]) => k.endsWith('CopyLocation'))?.[1] ??
        '';
    if (!name && !url) return [];
    return [{ uid: url || name || '-1', name: name || 'file', status: 'done', url, existing: true }];
}

const empty = (value) => value === undefined || value === null || value === '';

const REGION_OPTIONS = [
    { label: 'US', value: 'US' },
    { label: 'UK', value: 'UK' },
    { label: 'India', value: 'INDIA' },
];

// Strip HTML tags and decode common entities — used so min/max validation
// checks visible character count, not raw markup length.
const stripHtml = (html) =>
    String(html)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();

const numberRangeRule = ({ label, message, value, compare, text }) => ({
    validator: (_, input) => {
        if (empty(input)) return Promise.resolve();
        return compare(Number(input), Number(value))
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${value}`));
    },
});

function getComparableDateTime(value) {
    if (!value) return null;

    if (typeof value.valueOf === 'function') {
        const time = value.valueOf();
        return Number.isNaN(time) ? null : time;
    }

    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}

function getSiblingFieldName(currentName, compareField) {
    if (!Array.isArray(currentName)) return compareField;

    const nextName = [...currentName];
    nextName[nextName.length - 1] = compareField;
    return nextName;
}

const validationRulesByType = {
    noPastDate: ({ label, message }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const time = getComparableDateTime(value);
            if (time === null) return Promise.resolve();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return time < today.getTime()
                ? Promise.reject(new Error(message ?? `${label} cannot be a past date`))
                : Promise.resolve();
        },
    }),
    noFutureDate: ({ label, message }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const time = getComparableDateTime(value);
            if (time === null) return Promise.resolve();
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            return time > endOfDay.getTime()
                ? Promise.reject(new Error(message ?? `${label} cannot be a future date`))
                : Promise.resolve();
        },
    }),
    minAge: ({ label, message, value }) => ({
        validator: (_, input) => {
            if (empty(input)) return Promise.resolve();
            const time = getComparableDateTime(input);
            if (time === null) return Promise.resolve();
            const years = Number(value ?? 18);
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - years);
            cutoff.setHours(23, 59, 59, 999);
            return time > cutoff.getTime()
                ? Promise.reject(new Error(message ?? `Must be at least ${years} years old`))
                : Promise.resolve();
        },
    }),
    dateAfterField: ({ field, label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField) return Promise.resolve();

            const startFieldName = getSiblingFieldName(name, compareField);
            const startValue = form.getFieldValue(startFieldName);

            // Non-mandatory case:
            // If either date is empty, don't block submit.
            if (empty(startValue) || empty(input)) {
                return Promise.resolve();
            }

            const startTime = getComparableDateTime(startValue);
            const endTime = getComparableDateTime(input);

            if (startTime === null || endTime === null) {
                return Promise.resolve();
            }

            if (endTime < startTime) {
                return Promise.reject(
                    new Error(message ?? `${label} should not be before Start Date`)
                );
            }

            return Promise.resolve();
        },
    }),
    greaterThanField: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField) return Promise.resolve();

            const fromFieldName = getSiblingFieldName(name, compareField);
            const fromValue = form.getFieldValue(fromFieldName);

            if (empty(fromValue) || empty(input)) return Promise.resolve();

            const fromNum = Number(fromValue);
            const toNum = Number(input);

            if (isNaN(fromNum) || isNaN(toNum)) return Promise.resolve();

            return toNum < fromNum
                ? Promise.reject(new Error(message ?? `${label} must be greater than or equal to Experience From`))
                : Promise.resolve();
        },
    }),
    required: ({ field, message }) => ({
        validator: (_, value) => {
            let isEmpty = value === undefined || value === null || value === ''
                || (Array.isArray(value) && value.length === 0);
            if (!isEmpty && field?.type === 'text-editor') {
                isEmpty = stripHtml(value) === '';
            }
            return isEmpty
                ? Promise.reject(new Error(message ?? 'Mandatory Field'))
                : Promise.resolve();
        },
    }),
    email: ({ label, message }) => ({ type: 'email', message: message ?? `Enter valid ${label}` }),
    url: ({ label, message }) => ({ type: 'url', message: message ?? `Enter valid ${label}` }),
    number: ({ label, message }) => ({ pattern: /^[0-9]+$/, message: message ?? `${label} must be a number` }),
    phone: ({ message }) => ({ pattern: /^[0-9]{10}$/, message: message ?? 'Phone number must contain exactly 10 digits' }),
    len: ({ label, message, value }) => ({ len: Number(value), message: message ?? `${label} must be ${value} characters` }),
    pattern: ({ label, message, rule }) => ({
        pattern: new RegExp(rule.pattern ?? rule.value, rule.flags),
        message: message ?? `Enter valid ${label}`,
    }),
    min: ({ field, label, message, value }) => (
        field.type === 'number'
            ? numberRangeRule({ label, message, value, compare: (input, min) => input >= min, text: 'must be at least' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length >= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(message ?? `${label} must be at least ${value} characters`));
                },
            }
    ),
    max: ({ field, label, message, value }) => (
        field.type === 'number'
            ? numberRangeRule({ label, message, value, compare: (input, max) => input <= max, text: 'must not exceed' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length <= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(message ?? `${label} must not exceed ${value} characters`));
                },
            }
    ),
};

function validationToRule(validation, field, form, name) {
    const rule = typeof validation === 'string' ? { type: validation } : validation;
    const type = rule.type ?? rule.rule ?? rule.name;
    const value = rule.value ?? rule.length ?? rule.min ?? rule.max;
    const label = field.label ?? field.field ?? 'Field';
    const factory = validationRulesByType[type];

    return factory
        ? factory({ field, label, message: rule.message, rule, value, form, name })
        : rule;
}

function getRules(field, form, name, skipRequired = false) {
    const label = field.label ?? field.field ?? 'Field';
    const apiRules = field.validations ?? field.validation ?? field.rules ?? [];
    const hasApiRequired = apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required');
    const hasApiEmail = apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'email');
    const rules = [];

    if (!skipRequired && truthy(field.req) && !hasApiRequired) rules.push({ required: true, message: 'Mandatory Field' });
    if (field.type === 'email' && !hasApiEmail) rules.push({ type: 'email', message: `Enter valid ${label}` });
    if (field.type === 'number') rules.push({ pattern: /^[0-9]+$/, message: `${label} must be a number` });

    return [
        ...rules,
        ...apiRules.map((rule) => validationToRule(rule, field, form, name)),
    ];
}

function getValidationValue(field, type) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const rule = rules.find((item) => (typeof item === 'string' ? item : item?.type) === type);
    if (!rule || typeof rule === 'string') return undefined;
    return rule.value ?? rule.length ?? rule[type];
}

function getInputLimits(field) {
    const len = field.len ?? getValidationValue(field, 'len');
    const min = field.min ?? getValidationValue(field, 'min');
    const max = field.max ?? getValidationValue(field, 'max');

    if (field.type === 'number') {
        return {
            min: min ?? undefined,
            max: max ?? undefined,
        };
    }

    return {
        minLength: min ?? undefined,
        maxLength: field.maxLength ?? len ?? max ?? undefined,
    };
}

function getDisabledDate(field) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    for (const r of rules) {
        const type = typeof r === 'string' ? r : r?.type;
        if (type === 'noPastDate') {
            return (current) => {
                if (!current) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return getComparableDateTime(current) < today.getTime();
            };
        }
        if (type === 'noFutureDate') {
            return (current) => {
                if (!current) return false;
                const endOfDay = new Date();
                endOfDay.setHours(23, 59, 59, 999);
                return getComparableDateTime(current) > endOfDay.getTime();
            };
        }
        if (type === 'minAge') {
            const years = Number(r?.value ?? 18);
            return (current) => {
                if (!current) return false;
                const cutoff = new Date();
                cutoff.setFullYear(cutoff.getFullYear() - years);
                cutoff.setHours(23, 59, 59, 999);
                return getComparableDateTime(current) > cutoff.getTime();
            };
        }
    }
    return undefined;
}

function getDefaultPickerValue(field) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    for (const r of rules) {
        const type = typeof r === 'string' ? r : r?.type;
        if (type === 'minAge') {
            const years = Number(r?.value ?? 18);
            return dayjs().subtract(years, 'year');
        }
        if (type === 'noFutureDate') return dayjs();
        if (type === 'noPastDate') return dayjs();
    }
    return undefined;
}

function cleanValue(value, formatter) {
    if (!formatter) return value;
    if (formatter === 'phone' || formatter === 'digits') return value.replace(/\D/g, '').slice(0, 10);
    if (formatter === 'email' || formatter === 'noSpaces') return value.replace(/\s/g, '');
    if (formatter === 'name') return value.replace(/\s+/g, ' ').trimStart().replace(/\b\w/g, (char) => char.toUpperCase());
    return value;
}

function normalizeModule(moduleName = '') {
    const normalized = String(moduleName).toLowerCase();
    if (normalized === 'job') return 'jobs';
    if (normalized === 'candidate') return 'candidates';
    if (normalized === 'submissions') return 'submission';
    return normalized;
}

// buildDynamicFormData — builds the multipart FormData payload to send to the
// Auth gateway.
//
//   • File fields are appended as separate FormData parts. The part name is the
//     field's `fileKey` if configured, else the first segment of its path
//     (e.g. "passport.passportUploadName" → "passport"). Files nested inside an
//     addRow group array are collected from every row.
//   • All other fields are turned into the JSON "json" part by the shared,
//     config-driven payload engine (buildPayload). There is ZERO field-name or
//     module-specific logic here — payloadKey / dataType / payloadMode / template
//     are all read from the admin form-group config.
//
// Dynamic by design: any new field added in the admin panel is automatically
// included — no code change required. New modules work the same way.
function buildDynamicFormData(values, groups, extraPayload = {}) {
    const formData = new FormData();

    // New files → multipart parts, via the SHARED engine helper. On CREATE we
    // attach only the flat (non-repeatable) file fields — resume, photo, jobs
    // "file", passport, visa. Repeatable (addRow) document files are uploaded in
    // a follow-up update (see submit) because the create handler mishandles them;
    // the indexed update path is the reliable one. The document METADATA still
    // rides along in the JSON payload here, so the rows are created on insert and
    // the follow-up update just attaches each file to its row by index.
    collectFileParts(values, groups, { scope: 'flat' }).forEach(({ formKey, file }) => {
        formData.append(formKey, file);
    });

    // Config-driven JSON payload (file fields are skipped by the engine).
    // extraPayload carries linkage/context keys that are NOT form fields but the
    // downstream create requires (e.g. submission needs jobId + applicantId). These
    // are merged first so a real form field of the same name still wins.
    const payload = { ...extraPayload, ...buildPayload(values, groups) };
    payload.schemaVersion = 1;

    formData.append('json', JSON.stringify(payload));
    return formData;
}

function FieldControl({ field, form, name, moduleName, value, checked, onChange }) {
    const type = field.type ?? 'text';
    const disabled = !editable(field);
    const [dynamicOptions, setDynamicOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);

    // Fetch options dynamically when dataSource is master / module / lookup.
    // Static and undefined sources skip the API call and use field.options directly.
    useEffect(() => {
        const ds = field.dataSource;
        if (!['select', 'radio', 'checkbox'].includes(type)) return undefined;
        if (!ds || ds === 'static') return undefined;

        let cancelled = false;
        setOptionsLoading(true);

        async function fetchOptions() {
            try {
                let raw = [];
                if (ds === 'master' || ds === 'module') {
                    raw = await getDropdownValues(moduleName, field.field);
                } else if (ds === 'lookup') {
                    const params = new URLSearchParams({
                        collection: field.lookupCollection ?? '',
                        displayField: field.displayField ?? '',
                        valueField: field.valueField ?? '_id',
                    });
                    if (field.displayField2) params.set('displayField2', field.displayField2);
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
                    raw = json?.data ?? json ?? [];
                } else if (ds === 'group') {
                    const gn = (field.groupName ?? '').toLowerCase().trim();
                    if (gn === 'users' || gn === 'user') {
                        const json = await fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0');
                        const users = json?.data?.users ?? json?.users ?? json?.data ?? [];
                        raw = Array.isArray(users) ? users.map((u) => ({
                            label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.userName || u.email || String(u.userId),
                            value: u.userId ?? u.id,
                        })) : [];
                    }
                }
                if (!cancelled) setDynamicOptions(normalizeOptions(Array.isArray(raw) ? raw : []));
            } catch (err) {
                console.error(`[AddFormV1] Failed to fetch options for "${field.field}":`, err);
                if (!cancelled) setDynamicOptions([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }

        fetchOptions();
        return () => { cancelled = true; };
    }, [type, field.dataSource, field.field, field.lookupCollection, field.displayField, field.displayField2, field.valueField, field.groupName, moduleName]);

    const staticOptions = normalizeOptions(field.options ?? field.values ?? []);
    const options = dynamicOptions ?? staticOptions;

    // Select multiplicity is config-driven: multiSelect flag → explicit mode →
    // field.multiple → array dataType. No field-name list. "tags" is opt-in via
    // an explicit field.mode === "tags".
    const selectMode = field.multiSelect
        ? 'multiple'
        : (field.mode ?? (truthy(field.multiple) ? 'multiple' : undefined));
    const inferredSelectMode = selectMode ?? (effectiveDataType(field) === 'array' ? 'multiple' : undefined);

    const placeholder = field.placeholder ?? `${type === 'select' ? 'Select' : 'Enter'} ${String(field.label ?? '').toLowerCase()}`;
    const inputLimits = getInputLimits(field);
    // Sets the value in the form store first, then re-validates.
    // Required because FieldControl is a custom wrapper — Form.Item's injected
    // onChange never reaches the inner Ant Design components, so the store
    // would otherwise hold stale/undefined values when validation runs.
    const updateAndValidate = (nextValue) => {
        onChange?.(nextValue);

        setTimeout(() => {
            form.validateFields([name]).catch(() => { });
        }, 0);
    };

    const inputProps = {
        ...inputLimits,
        disabled,
        placeholder,
        prefix: field.icon ? null : fieldIcons[field.field],
        value: value ?? '',
        onChange: (event) => {
            const nextValue = field.formatter
                ? cleanValue(event.target.value, field.formatter)
                : event.target.value;

            updateAndValidate(nextValue);
        },
    };

    if (type === 'tagify') {
        return (
            <Select
                value={value}
                allowClear
                disabled={disabled}
                mode="tags"
                placeholder={placeholder}
                onChange={updateAndValidate}
            />
        );
    }
    if (type === 'select') {
        return (
            <Select
                value={value}
                allowClear
                disabled={disabled}
                loading={optionsLoading}
                mode={inferredSelectMode}
                options={options}
                placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onChange={updateAndValidate}
            />
        );
    }
    if (type === 'radio') {
        return (
            <Radio.Group
                value={value}
                disabled={disabled}
                options={options}
                onChange={(e) => updateAndValidate(e.target.value)}
            />
        );
    }
    if (type === 'checkbox') {
        if (options.length > 0) {
            return (
                <Checkbox.Group
                    value={value}
                    disabled={disabled}
                    options={options}
                    onChange={updateAndValidate}
                />
            );
        }
        return (
            <Checkbox checked={Boolean(checked ?? value)} disabled={disabled} onChange={(e) => updateAndValidate(e.target.checked)}>
                {field.checkboxLabel ?? field.label}
            </Checkbox>
        );
    }
    if (type === 'text-editor') {
        return (
            <TipTapEditor
                value={value ?? form.getFieldValue(name) ?? ''}
                disabled={disabled}
                onChange={(html) => updateAndValidate(html)}
            />
        );
    }
    if (type === 'textarea') return <TextArea {...inputProps} rows={field.rows ?? 3} />;
    if (type === 'date') {
        return (
            <DatePicker
                disabled={disabled}
                placeholder={placeholder}
                style={{ width: '100%' }}
                suffixIcon={<CalendarOutlined />}
                value={value ?? null}
                disabledDate={getDisabledDate(field)}
                defaultPickerValue={getDefaultPickerValue(field)}
                onChange={(date) => updateAndValidate(date)}
            />
        );
    }
    if (type === 'time') return <TimePicker disabled={disabled} placeholder={placeholder} style={{ width: '100%' }} value={value ?? null} onChange={(time) => updateAndValidate(time)} />;
    if (type === 'file') {
        const fileRules = field.validations ?? field.validation ?? field.rules ?? [];
        const fileTypeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileType');
        const fileSizeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileSize');
        const maxFilesRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'maxFiles');

        const acceptTypes = fileTypeRule?.value
            ? fileTypeRule.value.split(',').map((e) => `.${e.trim().replace(/^\./, '')}`).join(',')
            : undefined;
        const maxSizeMB = fileSizeRule ? Number(fileSizeRule?.value ?? 5) : undefined;
        // maxFiles validation rule takes top priority; otherwise use field.multiple/maxCount
        // (set via admin panel toggle), falling back to single-file mode.
        const maxFiles = maxFilesRule
            ? Number(maxFilesRule?.value ?? 1)
            : (truthy(field.multiple) ? (field.maxCount ?? 5) : 1);

        const fileList = Array.isArray(value) ? value : [];
        const canAddMore = fileList.length < maxFiles;

        const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        };

        const getFileIcon = (fileName) => {
            const ext = (fileName ?? '').split('.').pop()?.toLowerCase();
            if (ext === 'pdf') return <FilePdfOutlined className="v1-file-type-icon v1-file-icon-pdf" />;
            if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="v1-file-type-icon v1-file-icon-word" />;
            if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="v1-file-type-icon v1-file-icon-excel" />;
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FileImageOutlined className="v1-file-type-icon v1-file-icon-image" />;
            return <FileOutlined className="v1-file-type-icon v1-file-icon-default" />;
        };

        const handleFileDelete = (index) => {
            updateAndValidate(fileList.filter((_, i) => i !== index));
        };

        const handleFileView = (file) => {
            const url = file.url ?? file.thumbUrl;
            if (url) { window.open(url, '_blank'); return; }
            if (file.originFileObj instanceof File) {
                window.open(URL.createObjectURL(file.originFileObj), '_blank');
            }
        };

        const isMulti = maxFiles > 1;
        const remaining = maxFiles - fileList.length;

        const typeHint = acceptTypes
            ? acceptTypes.split(',').map((t) => t.replace('.', '').toUpperCase()).join(', ')
            : null;
        const sizeHint = maxSizeMB ? `up to ${maxSizeMB} MB` : null;
        const baseHint = [typeHint, sizeHint].filter(Boolean).join(' ');
        const dropHint = isMulti && fileList.length > 0
            ? [baseHint, `${remaining} of ${maxFiles} remaining`].filter(Boolean).join(' • ')
            : baseHint;
        const dropTitle = fileList.length > 0
            ? `Add ${isMulti ? 'more files' : 'file'}`
            : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

        const beforeUploadHandler = (file) => {
            if (fileList.length >= maxFiles) return Upload.LIST_IGNORE;
            if (acceptTypes) {
                const ext = (file.name.split('.').pop() ?? '').toLowerCase();
                const allowed = acceptTypes.split(',').map((t) => t.replace(/^\./, '').toLowerCase());
                if (!allowed.includes(ext)) {
                    message.error(fileTypeRule?.message ?? `Only ${fileTypeRule.value} files are allowed`);
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSizeMB !== undefined && file.size / 1024 / 1024 > maxSizeMB) {
                message.error(fileSizeRule?.message ?? `File must be smaller than ${maxSizeMB} MB`);
                return Upload.LIST_IGNORE;
            }
            return false;
        };

        // antd calls onChange once per file even in multi-select, each time with the
        // full accumulated newList. Slicing to maxFiles guards against racing additions.
        const handleUploadChange = ({ fileList: newList }) => {
            updateAndValidate(newList.slice(0, maxFiles));
        };

        const multiUploadProps = {
            accept: acceptTypes,
            fileList,
            showUploadList: false,
            multiple: true,
            maxCount: maxFiles,
            beforeUpload: beforeUploadHandler,
            onChange: handleUploadChange,
            disabled,
        };

        const fileRows = fileList.map((file, index) => (
            <div key={file.uid ?? index} className="v1-file-item">
                <div className="v1-file-item-icon">{getFileIcon(file.name)}</div>
                <div className="v1-file-item-info">
                    <span className="v1-file-item-name">{file.name}</span>
                    <span className="v1-file-item-meta">
                        {file.existing
                            ? 'Previously uploaded'
                            : `${formatFileSize(file.size)}${file.size ? ' • ' : ''}Uploaded just now`}
                    </span>
                </div>
                <Space size={6} className="v1-file-item-actions">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleFileView(file)}
                        className="v1-file-view-btn"
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(index)}
                        className="v1-file-delete-btn"
                    />
                </Space>
            </div>
        ));

        if (isMulti) {
            const multiTitle = fileList.length > 0
                ? `Upload more ${field.label ?? 'files'}`
                : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

            return (
                <div className="v1-file-upload-wrapper">
                    {/* Centered dropzone — always at top in multiple mode */}
                    {canAddMore && (
                        <Upload {...multiUploadProps} style={{ width: '100%', display: 'block' }}>
                            <div className={`v1-upload-dropzone v1-upload-dropzone-center${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                <div className="v1-upload-dropzone-title-row">
                                    <CloudUploadOutlined className="v1-upload-cloud-icon v1-upload-cloud-icon-sm" />
                                    <span className="v1-upload-dropzone-title v1-upload-dropzone-title-blue">{multiTitle}</span>
                                </div>
                                <span className="v1-upload-dropzone-sub">Drag & drop or click to upload</span>
                                {baseHint && <span className="v1-upload-dropzone-sub">{baseHint}</span>}
                            </div>
                        </Upload>
                    )}

                    {/* Uploaded file rows — 2-column grid */}
                    {fileList.length > 0 && (
                        <div className="v1-file-grid">
                            {fileRows}
                        </div>
                    )}

                    {/* "+ Add more files" text link at the bottom */}
                    {canAddMore && fileList.length > 0 && (
                        <Upload {...multiUploadProps} style={{ display: 'inline-block' }}>
                            <Button type="link" size="small" icon={<PlusOutlined />} className="v1-add-more-files-btn">
                                Add more files
                            </Button>
                        </Upload>
                    )}
                </div>
            );
        }

        return (
            <div className="v1-file-upload-wrapper">
                {/* Single mode: compact file row at field height */}
                {fileList.length > 0 && <div className="v1-file-rows-single">{fileRows}</div>}

                {canAddMore && (
                    <Upload
                        accept={acceptTypes}
                        fileList={fileList}
                        showUploadList={false}
                        multiple={false}
                        maxCount={1}
                        beforeUpload={beforeUploadHandler}
                        onChange={handleUploadChange}
                        disabled={disabled}
                        style={{ width: '100%', display: 'block' }}
                    >
                        <div className={`v1-upload-dropzone v1-upload-dropzone-single${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                            <CloudUploadOutlined className="v1-upload-cloud-icon" />
                            <span className="v1-upload-dropzone-title">{dropTitle}</span>
                            {dropHint && <span className="v1-upload-dropzone-sub">— {dropHint}</span>}
                        </div>
                    </Upload>
                )}
            </div>
        );
    }

    return <Input {...inputProps} type={type === 'number' ? 'number' : type} />;
}

function SectionTitle({ icon, title }) {
    return (
        <Space size={10} align="center">
            <div className="v1-section-icon-box">{icon}</div>
            <Text strong className="v1-section-label">{title}</Text>
        </Space>
    );
}

function FieldLabel({ field, label }) {
    if (!label) return label;
    if (!truthy(field?.infoEnabled) || empty(field?.infoText)) return label;
    return (
        <span className="v1-field-label-with-info">
            <span>{label}</span>
            <Tooltip title={field.infoText}>
                <InfoCircleOutlined className="v1-field-info-icon" />
            </Tooltip>
        </span>
    );
}

function GroupInfoMessage({ group }) {
    if (!truthy(group?.infoEnabled) || empty(group?.infoText)) return null;
    return <Text type="secondary" className="v1-group-info-message">{group.infoText}</Text>;
}

export default function AddFormV1({
    moduleName,
    action,
    clientId,
    region,
    requireClientScope = false,
    formGroups: providedFormGroups,
    loading: providedLoading = false,
    error: providedError = '',
    breadcrumbItems = [],
    // formType — controls the mode of operation:
    //   'add'    → Add Form (default, existing behavior)
    //   'search' → Search Form (lighter validation, Reset button, no auto-create)
    formType = 'add',
    submitText: propSubmitText,
    cancelPath = '/candidates',
    onSubmit,
    // initialRecord — prefill flat form fields from a plain record object.
    // Matched by field.field / payloadKey / dot-path; best-effort, never required.
    initialRecord = null,
    // prefillFromUrl — when set (quickSubmit flow), AddFormV1 reads the route to
    // decide the prefill source and the job/candidate linkage entirely on its own:
    //   • isFromSubmission=true + submissionId → read the submissions collection
    //   • otherwise candidateId                → read the candidates collection
    // Both resolve through getFormGroups(id) (field.value + group.rows embedded) —
    // no separate detail-view API. The linkage the submission create requires
    // (applicantId + jobId) is derived from the same URL params.
    prefillFromUrl = false,
    // extraPayload — context/linkage keys merged into the create payload that are
    // NOT form fields (e.g. submission needs { jobId, applicantId }). Kept out of
    // the form so the config stays generic. Ignored when prefillFromUrl derives
    // its own linkage from the route.
    extraPayload = null,
}) {
    const isSearch = formType === 'search';
    const submitText = propSubmitText ?? (isSearch ? 'Search' : 'Create');
    const navigate = useNavigate();
    const routeParams = useParams();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const [collapsed, setCollapsed] = useState({});
    const [apiFormGroups, setApiFormGroups] = useState([]);
    const [apiLoading, setApiLoading] = useState(Boolean(moduleName));
    const [apiError, setApiError] = useState('');
    const [searchFormLabel, setSearchFormLabel] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);
    const [scopeClientId, setScopeClientId] = useState(clientId ?? '');
    const [scopeRegion, setScopeRegion] = useState(region ?? '');
    // URL-driven prefill state — populated when prefillFromUrl is set. Holds the
    // source record's form groups (candidate OR submission) with values embedded.
    const [prefillGroups, setPrefillGroups] = useState(null);
    const [prefillLoading, setPrefillLoading] = useState(false);
    const formGroups = moduleName ? apiFormGroups : (providedFormGroups ?? emptyGroups);
    const loading = moduleName ? apiLoading : providedLoading;
    const error = moduleName ? apiError : providedError;
    const effectiveClientId = clientId || scopeClientId;
    const effectiveRegion = region || scopeRegion;
    const waitingForClientScope = requireClientScope && !effectiveClientId;
    const groups = useMemo(() => normalizeGroups(formGroups), [formGroups]);

    useEffect(() => {
        if (!requireClientScope) return;
        getAdminClients({ limit: 200 })
            .then(setClientOptions)
            .catch(() => setClientOptions([]));
    }, [requireClientScope]);

    useEffect(() => {
        if (!moduleName) return undefined;
        if (waitingForClientScope) {
            setApiFormGroups([]);
            setApiLoading(false);
            setApiError('');
            return undefined;
        }

        let cancelled = false;

        async function fetchFormGroups() {
            try {
                setApiLoading(true);
                setApiError('');
                const data = isSearch
                    ? await getCustomForms(moduleName, 'search', action)
                    : await getFormGroups({ module: moduleName, action, clientId: effectiveClientId, region: effectiveRegion });
                if (!cancelled) {
                    setApiFormGroups(extractGroups(isSearch ? data?.[0] : data));
                    if (isSearch) setSearchFormLabel(data?.[0]?.label || '');
                }
            } catch (err) {
                console.error(`Failed to fetch ${moduleName} form groups:`, err);
                if (!cancelled) setApiError(err?.message || 'Failed to fetch form groups');
            } finally {
                if (!cancelled) setApiLoading(false);
            }
        }

        fetchFormGroups();

        return () => {
            cancelled = true;
        };
    }, [moduleName, action, isSearch, effectiveClientId, effectiveRegion, waitingForClientScope]);

    // ── URL-driven prefill source + linkage ──
    // Decode the route once. The submission flow arrives at
    //   /submission-create/:jobId?candidateId=…&isFromSubmission=…[&submissionId=…]
    //   • isFromSubmission=true + submissionId → prefill from the submissions record
    //   • otherwise candidateId                → prefill from the candidates record
    const urlPrefill = useMemo(() => {
        if (!prefillFromUrl) return null;

        const candidateId = searchParams.get('candidateId') || '';
        const submissionId = searchParams.get('submissionId') || '';
        const fromSubmission = String(searchParams.get('isFromSubmission')) === 'true';
        const jobId = (routeParams.jobId && routeParams.jobId !== '_')
            ? routeParams.jobId
            : (searchParams.get('jobId') || '');

        const source = (fromSubmission && submissionId)
            ? { module: 'submissions', id: submissionId }
            : (candidateId ? { module: 'candidates', id: candidateId } : null);

        // Linkage the submission create requires (NOT form fields).
        const linkage = {};
        if (candidateId) linkage.applicantId = candidateId;
        if (jobId) linkage.jobId = jobId;

        return { source, linkage: Object.keys(linkage).length ? linkage : null };
    }, [prefillFromUrl, searchParams, routeParams]);

    // ── Prefill fetch: load the source record's form groups with embedded values ──
    // The backend embeds field.value for scalar fields and group.rows for addRow
    // groups (workExperience, educationDetails, documents). Single source of truth
    // for BOTH the candidate and submission flows — no detail-view API call.
    useEffect(() => {
        const source = urlPrefill?.source;
        if (!source) return undefined;

        let cancelled = false;

        async function fetchPrefill() {
            try {
                setPrefillLoading(true);
                const data = await getFormGroups({ module: source.module, id: source.id });
                if (!cancelled) setPrefillGroups(extractGroups(data));
            } catch (err) {
                console.error('Failed to fetch prefill data:', err);
                if (!cancelled) setPrefillGroups([]);
            } finally {
                if (!cancelled) setPrefillLoading(false);
            }
        }

        fetchPrefill();

        return () => { cancelled = true; };
    }, [urlPrefill]);

    // addRow rows from the prefill source, keyed by group.payloadKey (e.g.
    // "workExperience"). Submission addRow groups that share the same payloadKey
    // receive these rows as their Form.List initialValue at mount. Works for both
    // sources because the DB array element keys are identical (companyName,
    // jobTitle, qualification, …).
    const addRowPrefillsByKey = useMemo(() => {
        if (!prefillGroups) return {};
        return Object.fromEntries(
            prefillGroups
                .filter(g => g.addRow && Array.isArray(g.rows) && g.rows.length > 0)
                .map(g => [g.payloadKey || g.name, g.rows]),
        );
    }, [prefillGroups]);

    // Flat record extracted from the prefill source's scalar fields — prefills
    // form fields whose field.field (or payloadKey) matches a source field.
    const prefillFlatRecord = useMemo(() => {
        if (!prefillGroups) return null;
        const rec = {};
        prefillGroups.forEach(group => {
            if (group.addRow) return;
            (group.fields ?? []).forEach(field => {
                if (!field.field || field.value === undefined || field.value === null) return;
                rec[field.field] = field.value;
                if (field.payloadKey && field.payloadKey !== field.field) {
                    rec[field.payloadKey] = field.value;
                }
            });
        });
        return Object.keys(rec).length > 0 ? rec : null;
    }, [prefillGroups]);

    // ── Prefill from initialRecord / prefillFlatRecord (best-effort) ──
    // Matches each non-file scalar field by field.field → payloadKey on the
    // source record and sets it via setFieldsValue. addRow groups are seeded via
    // Form.List initialValue (see getAddRowInitialValue below) — setFieldsValue
    // on a Form.List that mounted empty only keeps the last row.
    useEffect(() => {
        const sourceRecord = initialRecord ?? prefillFlatRecord;
        if (!sourceRecord || loading || groups.length === 0) return;
        const values = {};
        groups.forEach((group) => {
            if (group.addRow) return;
            (group.fields ?? []).forEach((field) => {
                if (!field.field) return;
                // prefillFrom (admin config) → field → payloadKey, dotted or nested.
                const stored = pickPrefillValue(sourceRecord, field);
                if (stored === undefined || stored === null) return;
                if (field.type === 'file') {
                    const fl = toFileListValue(stored);
                    if (fl.length > 0) values[field.field] = fl;
                } else {
                    values[field.field] = buildInitialValue(field, stored);
                }
            });
        });
        if (Object.keys(values).length > 0) form.setFieldsValue(values);
    }, [initialRecord, prefillFlatRecord, loading, groups, form]);

    // Linkage merged into the create payload: URL-derived when prefillFromUrl,
    // else the explicit extraPayload prop.
    const scopePayload = requireClientScope && effectiveClientId ? { clientId: effectiveClientId } : {};
    const effectiveExtraPayload = urlPrefill?.linkage ?? { ...scopePayload, ...(extraPayload ?? {}) };

    // True once both the form groups and (when needed) the prefill source data are
    // ready. The Form.List components must NOT mount until this is true so that
    // getAddRowInitialValue below provides the correct initialValue at mount —
    // antd ignores initialValue changes after a Form.List has already mounted.
    const isFormReady = !waitingForClientScope && !loading && (!urlPrefill?.source || (prefillGroups !== null && !prefillLoading));

    // Build the Form.List initialValue for an addRow group. When candidate prefill
    // data is available the group's payloadKey (e.g. "workExperience") is looked up
    // in addRowPrefillsByKey and the stored rows are mapped through buildInitialValue
    // so antd controls receive the right shape. Falls back to a single empty row.
    const getAddRowInitialValue = (group) => {
        const gKey = group.payloadKey || group.name;
        const rows = addRowPrefillsByKey[gKey];
        if (!rows || rows.length === 0) return [{}];
        return rows.map(row => {
            const rowVals = {};
            (group.fields ?? []).forEach(field => {
                if (!field.field || field.type === 'file') return;
                const val = pickPrefillValue(row, field);
                if (val !== undefined && val !== null) {
                    rowVals[field.field] = buildInitialValue(field, val);
                }
            });
            return Object.keys(rowVals).length > 0 ? rowVals : {};
        });
    };

    const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleReset = () => {
        form.resetFields();
        // Also reset collapsed state to default (all expanded)
        setCollapsed({});
    };

    const submit = async (values) => {
        if (onSubmit) {
            await onSubmit(values);
            return;
        }

        if (!moduleName) {
            console.log('Form values:', values);
            return;
        }

        try {
            setSubmitLoading(true);
            const response = await createModuleRecord(moduleName, buildDynamicFormData(values, groups, effectiveExtraPayload ?? {}));
            const success = response?.status !== false;

            if (!success) {
                message.error(response?.error || response?.message || response?.data || 'Something went wrong');
                return;
            }

            // Repeatable (addRow) document files are attached in a follow-up update
            // through the reliable indexed path (documents[0], documents[1], …). The
            // rows themselves were created on insert (metadata is in the create JSON),
            // so this only uploads each file to its row by index. Runs only when the
            // form actually carried addRow file uploads, so plain records and jobs
            // stay single-request.
            const addRowFileParts = collectFileParts(values, groups, { scope: 'addRow', indexed: true });
            const newRecordId = response?.id ?? response?.data?.id ?? response?.data?._id ?? response?._id;
            if (addRowFileParts.length > 0 && newRecordId) {
                try {
                    await updateRecord(moduleName, newRecordId, { ...(effectiveExtraPayload ?? {}), ...buildPayload(values, groups) }, addRowFileParts);
                } catch (uploadErr) {
                    console.error('Document file attach (post-create update) failed:', uploadErr);
                    message.warning('Record created, but attaching document files failed. Edit the record to re-upload.');
                }
            }

            message.success(response?.message || response?.data || `${moduleName} created successfully`);
            form.resetFields();

            const module = normalizeModule(moduleName);
            if (module === 'jobs') navigate('/jobs');
            else if (module === 'candidates') navigate('/candidates');
            else if (module === 'onboarding') navigate('/onboarding');
            else if (module === 'submission') navigate('/submissions');
        } catch (err) {
            console.error(`Failed to create ${moduleName}:`, err);
            message.error(err?.data?.error || err?.data?.message || err?.message || 'Failed to create record');
        } finally {
            setSubmitLoading(false);
        }
    };

    const getFieldDependencies = (field, currentName) => {
        const rules = field.validations ?? field.validation ?? field.rules ?? [];
        return rules
            .filter((rule) => {
                const type = typeof rule === 'string' ? rule : rule?.type;
                return type === 'dateAfterField' || type === 'greaterThanField';
            })
            .map((rule) => {
                const compareField = rule.value ?? rule.compareField ?? rule.field;
                return compareField ? getSiblingFieldName(currentName, compareField) : null;
            })
            .filter(Boolean);
    };

    const evaluateShowIf = (showIf, getFieldValue, siblingPrefix) => {
        if (!showIf?.field) return true;
        const path = siblingPrefix != null
            ? [...(Array.isArray(siblingPrefix) ? siblingPrefix : [siblingPrefix]), showIf.field]
            : [showIf.field];
        const val = getFieldValue(path);
        switch (showIf.operator) {
            case 'eq': return String(val ?? '') === String(showIf.value ?? '');
            case 'neq': return String(val ?? '') !== String(showIf.value ?? '');
            case 'truthy': return val !== undefined && val !== null && val !== '' && val !== false;
            case 'falsy': return val === undefined || val === null || val === '' || val === false;
            default: return true;
        }
    };

    const [combineToggles, setCombineToggles] = useState({});

    const fieldKey = (field) => String(field?.field ?? '');

    const renderField = (field, namePrefix, listName, opts = {}) => {
        // Split dotted keys into array form so AntD Form nests values correctly:
        // "passport.passportNumber" → ['passport','passportNumber']
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : null;
        const parsedField = fieldParts ?? field.field;
        const name = namePrefix !== undefined
            ? [namePrefix, ...(fieldParts ?? [field.field])]
            : parsedField;
        // Inside a Form.List the relative name [rowIndex, fieldName] is correct for
        // Form.Item registration, but form.getFieldValue / cross-field validators need
        // the absolute path [listName, rowIndex, fieldName] to find the value in the
        // form store. Build that here so validators and validateFields use the right path.
        const absoluteName = (listName !== undefined && namePrefix !== undefined)
            ? [listName, ...(Array.isArray(name) ? name : [name])]
            : name;
        // Checkbox.Group (when options are configured) uses value=[]; single boolean checkbox uses checked=bool.
        const isCheckboxGroup = field.type === 'checkbox' && (
            (field.options && field.options.length > 0) ||
            (field.dataSource && field.dataSource !== 'static')
        );
        const valuePropName = (!isCheckboxGroup && field.type === 'checkbox') ? 'checked' : 'value';
        // Inline (combined) fields show no per-box label — the combined card prints a
        // single label and the boxes sit side by side (e.g. From / To / Currency).
        const fieldLabel = opts.inline
            ? ''
            : (!isCheckboxGroup && field.type === 'checkbox')
                ? ''
                : <FieldLabel field={field} label={field.label} />;
        const dependencies = getFieldDependencies(field, absoluteName);
        const mdSpan = opts.inline ? 24 : ((field.type === 'file' && truthy(field.multiple)) || field.type === 'text-editor' ? 24 : (field.col ?? 12));
        // Sibling path prefix for evaluateShowIf: [listName, rowIndex] in a Form.List, null otherwise.
        const showIfPrefix = (listName !== undefined && namePrefix !== undefined) ? [listName, namePrefix] : null;

        // Config-driven default for the Add form: a top-level field (not inside a
        // Form.List) with a configured defaultValue mounts pre-selected. Any prefill
        // (initialRecord / quick-submit) runs through setFieldsValue, which overrides
        // initialValue — so this only affects a fresh Add with no incoming value.
        const initialValue = (namePrefix === undefined && field.type !== 'file'
            && field.defaultValue !== undefined && field.defaultValue !== '')
            ? field.defaultValue : undefined;

        const fieldItem = (
            <Form.Item
                label={fieldLabel}
                name={name}
                required={truthy(field.req)}
                rules={getRules(field, form, absoluteName, isSearch)}
                dependencies={dependencies}
                valuePropName={valuePropName}
                initialValue={initialValue}
                getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                validateTrigger={['onBlur', 'onChange']}
            >
                <FieldControl field={field} form={form} name={absoluteName} moduleName={moduleName} />
            </Form.Item>
        );

        // Inside a combined block, each box's width follows its combineWidth weight
        // (flex-grow), so the pack can be sized by ratio (e.g. 2:1, 1:2:3). Unset → 1.
        const inlineStyle = opts.inline
            ? { flex: `${Number(field.combineWidth) > 0 ? Number(field.combineWidth) : 1} 1 0`, maxWidth: 'none' }
            : undefined;

        if (field.showIf?.field) {
            return (
                <Form.Item key={field.field} shouldUpdate noStyle>
                    {({ getFieldValue }) =>
                        evaluateShowIf(field.showIf, getFieldValue, showIfPrefix)
                            ? <Col xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItem}</Col>
                            : null
                    }
                </Form.Item>
            );
        }
        return <Col key={field.field} xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItem}</Col>;
    };

    const renderFields = (fields, namePrefix, listName) => {
        const used = new Set();
        const out = [];
        fields.forEach((field) => {
            const combineKey = field.combineKey?.trim?.();
            if (!combineKey) {
                out.push(renderField(field, namePrefix, listName));
                return;
            }
            const packKey = `${listName ?? 'root'}:${namePrefix ?? 'root'}:${combineKey}`;
            if (used.has(packKey)) return;
            const pack = fields.filter((f) => f.combineKey === combineKey);
            pack.forEach((f) => used.add(`${listName ?? 'root'}:${namePrefix ?? 'root'}:${f.combineKey}`));

            const label = pack.find((f) => f.combineLabel)?.combineLabel ?? field.combineLabel ?? field.label;
            const toggleTarget = pack.find((f) => f.combineToggleTarget)?.combineToggleTarget;
            const toggleLabel = pack.find((f) => f.combineToggleLabel)?.combineToggleLabel ?? 'Show range';
            const toggleStateKey = packKey;
            const targetField = toggleTarget ? pack.find((f) => fieldKey(f) === toggleTarget) : null;
            const targetParts = targetField?.field?.includes?.('.') ? targetField.field.split('.') : [targetField?.field].filter(Boolean);
            const targetName = targetField
                ? ((listName !== undefined && namePrefix !== undefined)
                    ? [listName, namePrefix, ...targetParts]
                    : (targetParts.length > 1 ? targetParts : targetField.field))
                : null;

            // Layout mirrors the legacy combined field: one label, the boxes inline on
            // a single row, and the range toggle BELOW. Toggle off → only the primary
            // (target hidden); toggle on → the target box appears alongside. The wider
            // packs (with currency/unit) take a full row; a simple From/To pair is half.
            const primaryReq = pack.some((f) => truthy(f.req));
            const comboSpan = pack.length >= 3 ? 24 : 12;
            out.push(
                <Col key={packKey} xs={24} md={comboSpan}>
                    <Form.Item label={label} required={primaryReq} colon={false} className="v1-combine-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const hasTargetValue = targetName ? !empty(getFieldValue(targetName)) : false;
                                const checked = combineToggles[toggleStateKey] ?? hasTargetValue;
                                const visiblePack = toggleTarget && !checked
                                    ? pack.filter((f) => fieldKey(f) !== toggleTarget)
                                    : pack;
                                return (
                                    <>
                                        <Row gutter={[8, 0]} align="top" wrap={false} className="v1-combine-row">
                                            {visiblePack.map((f) => renderField(f, namePrefix, listName, { inline: true }))}
                                        </Row>
                                        {toggleTarget && (
                                            <div className="v1-combine-range">
                                                <Switch
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(next) => setCombineToggles((prev) => ({ ...prev, [toggleStateKey]: next }))}
                                                />
                                                <Text type="secondary" className="v1-combine-toggle-label">{toggleLabel}</Text>
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </Col>,
            );
        });
        return out;
    };

    const renderGroup = (group, index) => {
        const key = group.name ?? group.id ?? String(index);
        const body = group.addRow ? (
            <Form.List
                name={group.name ?? key}
                initialValue={getAddRowInitialValue(group)}
            >
                {(rows, { add, remove }) => (
                    <div className="v1-addrow-wrapper">
                        <div className="v1-addrow-topbar">
                            <Button
                                type="link"
                                icon={<PlusOutlined />}
                                onClick={() => add()}
                                className="v1-addrow-add-btn"
                            >
                                {group.addLabel ?? `Add ${group.label ?? 'Row'}`}
                            </Button>
                        </div>

                        {rows.map(({ key: rowKey, name }, rowIndex) => (
                            <div key={rowKey} className="v1-addrow-item">
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                    disabled={rows.length === 1}
                                    className="v1-addrow-delete-btn"
                                />

                                <Row gutter={[24, 0]} align="top">
                                    {renderFields(group.fields, name, group.name ?? key)}
                                </Row>
                            </div>
                        ))}
                    </div>
                )}
            </Form.List>
        ) : (
            <Row gutter={[24, 0]}>{renderFields(group.fields)}</Row>
        );

        return (
            <Card
                key={key}
                className="v1-card v1-edit-card"
                title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={isSearch ? (searchFormLabel || group.label) : (group.label ?? group.name ?? 'Details')} />}
                extra={(
                    <Space size={8} align="center" className="v1-group-actions">
                        <GroupInfoMessage group={group} />
                        <Button
                            type="text"
                            size="small"
                            icon={collapsed[key] ? <DownOutlined /> : <UpOutlined />}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggle(key);
                            }}
                        />
                    </Space>
                )}
            >
                <div style={{ display: collapsed[key] ? 'none' : 'block' }}>{body}</div>
            </Card>
        );
    };

    return (
        <main>
            {breadcrumbItems.length > 0 && <AppBreadcrumb items={breadcrumbItems} />}
            <div className="v1-edit-page">
                {requireClientScope && (
                    <Card className="v1-card v1-edit-card v1-client-gate-card">
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <SectionTitle icon={<UserOutlined />} title="Select Client" />
                            <Text type="secondary">Choose the client before loading the Jobs form. The form fields are loaded for that client.</Text>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={14}>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select client"
                                        style={{ width: '100%' }}
                                        value={effectiveClientId || undefined}
                                        optionFilterProp="label"
                                        options={clientOptions.map((c) => ({
                                            value: c._id ?? c.id,
                                            label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                                        })).filter((c) => c.value)}
                                        onChange={(value) => setScopeClientId(value ?? '')}
                                    />
                                </Col>
                            </Row>
                        </Space>
                    </Card>
                )}
                <div className="v1-page-inner">
                    <Spin spinning={!isFormReady && !waitingForClientScope} description="Loading form fields...">
                        <Form
                            form={form}
                            layout="vertical"
                            colon={false}
                            className="v1-form"
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="v1-required-mark"> *</span>}
                                </>
                            )}
                            onFinish={submit}
                        >
                            {isFormReady && groups.map(renderGroup)}
                            {isFormReady && !error && groups.length === 0 && (
                                <Alert type="info" showIcon message={isSearch ? "No search configuration found." : "No form fields are configured."} />
                            )}
                            {isFormReady && isSearch && (
                                <div className="v1-search-form-actions">
                                    <Space size={12}>
                                        <Button onClick={handleReset} disabled={!groups.length}>
                                            Reset
                                        </Button>
                                        <Button onClick={() => navigate(cancelPath)}>
                                            Back
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            disabled={submitLoading || !groups.length}
                                            loading={submitLoading}
                                        >
                                            {submitText}
                                        </Button>
                                    </Space>
                                </div>
                            )}
                        </Form>
                    </Spin>
                </div>

                {!isSearch && (
                    <div className="v1-footer">
                        <Text type="secondary">All Unsaved Changes will be lost if you cancel and reload.</Text>
                        <Space size={12}>
                            <Button onClick={() => navigate(cancelPath)}>Cancel</Button>
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                disabled={!isFormReady || submitLoading || !groups.length}
                                loading={submitLoading}
                            >
                                {submitText}
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </main>
    );
}
